import { client } from "../../hooks/Client"
import { useFirestoreGeneralOrderBy, useFirestoreGeneral} from "../../firebase/useFirestore"
import { useHistory } from "react-router-dom";
import ScrollToTop from "../../hooks/ScrollToTop";
import { db } from "../../firebase/config";
import useSettings from "../../hooks/Settings";
import NextStepContainer from "../../components/common/NextStepContainer.jsx";
import { Auth } from '../../StateManagment/Auth';
import { useContext, useState, useEffect, useMemo } from "react";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { v4 as uuid } from 'uuid';
import { timestamp } from "../../firebase/config";
import RandomColorGenerator from "../../hooks/RandomColorGenerator";
import PreviousStepContainer from "../../components/common/PreviousStepContainer";
import OutputIsSyncedTitle from "../../components/syncronisations/OutputIsSyncedTitle";
import OutputIsSyncedDelete from "../../components/syncronisations/OutputIsSyncedDelete";
import PageHeader from "../../components/layout/PageHeader.jsx";
import SavedNotice from "../../components/common/SavedNotice"
import { DeccaContext } from "../../StateManagment/DeccaContext.js";
import Location from "../../hooks/Location"
import DragIconTableData from "../../components/common/DragIconTableData.jsx";
import Table from "../../components/common/table/Table.jsx";
import { outputUnits, outputUnitLabels, defaultOutputUnitBySubtype } from "../../helpers/dataHelpers/activityCategories";
import { measurementMethodLabels, DEFAULT_MEASUREMENT_METHOD, allowedMeasurementMethods } from "../../helpers/dataHelpers/measurementMethods";
import Tooltip from "../../components/common/Tooltip";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import OutputGoalModal from "../../components/outputs/OutputGoalModal.jsx";
import OutputGoalCell from "../../components/outputs/OutputGoalCell.jsx";
import DatalistOutputUnits from "../../components/activities/DatalistOutputUnits.jsx";

const AddOutputs = () => {

     // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const {
        setSelectedActivity,
        selectedActivity
      } = useContext(DeccaContext);

    // States
    const [dragArray, setDragArray] = useState([]);
    const [activityTitle, setActivityTitle] = useState('')
    const [goalModalOpen, setGoalModalOpen] = useState(false)
    const [goalOutput, setGoalOutput] = useState(/** @type {object | null} */ (null))
    
    // Hooks
    const history = useHistory()
    ScrollToTop()
    const randomColor = RandomColorGenerator()
    const activityID = Location()[3]
    const { personasOn, housevisits } = useSettings()
    const methodOptions = allowedMeasurementMethods({ personasOn, housevisits })

    // Firestore
    const outputsRaw = useFirestoreGeneralOrderBy('Outputs', 'ActivityID', activityID || "", 'Position', 'asc')
    const activities = useFirestoreGeneral('Activities', 'ID', activityID || "")

    // Hide soft-deleted outputs (ADR-008)
    const outputs = useMemo(() => (outputsRaw || []).filter((o) => !o.Deleted), [outputsRaw])

    // AI
    useEffect(() => {
        setSelectedActivity(activityID);
    }, [activityID])

    // Set activity title and AI context
    useEffect(() => {
        if (activities && activities.length > 0) {
            setActivityTitle(activities[0].Activity);
            // Set AI context for the current activity
            setSelectedActivity({
                id: activityID,
                name: activities[0].Activity
            });
        }
    }, [activities]);

    // Whenever Firestore data changes, update dragArray state
    useEffect(() => {
        setDragArray(outputs);
    }, [outputs]);

    // DRAG & DROP: reorder items and update Firestore
    const handleDragEnd = (result) => {
        if (!result.destination) return;
    
        const items = Array.from(dragArray);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);
    
        // Update local state to reflect the reorder immediately
        setDragArray(items);
    
        // Now update Firestore with new positions
        items.forEach((item, idx) => {
          db.collection('Outputs')
            .doc(item.docid) // assuming .docid is stored for each doc
            .update({ Position: idx + 1 });
        });
    
        // Show "saved" icon
        setSaved('flex');
      };

    // Add output
    const addOutput = (e) => {

        db.collection('Outputs')
        .doc()
        .set({
            ID: uuid(),
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Title: '',
            Description: '',
            // Standardised unit for cross-org comparison (ADR-010). Default from
            // the activity subtype; the free-text Title stays the human label.
            Unit: defaultOutputUnitBySubtype[activities[0]?.activitySubtype] || 'sessions',
            // How this output is measured — the TOC rule (ADR-011). Default: via
            // the event form; an org can switch it to attendance / huisbezoek.
            MeasurementMethod: DEFAULT_MEASUREMENT_METHOD,
            Position: outputs.length + 1,
            Color: randomColor,
            ActivityID: activityID,
            LinkParticipantsToOutputs: 'true'
        })
    }

    // Update description
    const descriptionHandler = (e) => {
        const description = e.target.value
        const docid = e.target.dataset.docid

        db.collection('Outputs')
        .doc(docid)
        .update({ Description: description })
        .then(() => { setSaved('flex') })
    }

    // Update standardised unit
    const unitHandler = (e) => {
        const unit = e.target.value
        const docid = e.target.dataset.docid

        db.collection('Outputs')
        .doc(docid)
        .update({ Unit: unit })
        .then(() => { setSaved('flex') })
    }

    // Update measurement method (ADR-011)
    const methodHandler = (e) => {
        const method = e.target.value
        const docid = e.target.dataset.docid

        db.collection('Outputs')
        .doc(docid)
        .update({ MeasurementMethod: method })
        .then(() => { setSaved('flex') })
    }

    const openGoalModal = (output) => {
        setGoalOutput(output)
        setGoalModalOpen(true)
    }

    const tableColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'output', label: 'OUTPUT' },
        { key: 'unit', label: 'EENHEID', searchable: false },
        { key: 'method', label: 'MEETMETHODE', searchable: false },
        { key: 'description', label: 'BESCHRIJVING', searchable: false },
        { key: 'goal', label: 'DOEL', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

  return (
    <div className="main">
        <div className="main-container" >
            <PageHeader 
                heading={'Outputs'}
                subHeading={`${activityTitle}`}
                description={`In deze stap ga je de kwantitatieve impact (outputs) benoemen die je gaat bijhouden voor deze activiteit.`}
                AI={true}
            />
            <datalist id="output-unit-suggestions">
                <DatalistOutputUnits activitySubtype={activities[0]?.activitySubtype} />
            </datalist>
            <ReadOnlyGuard edit="strategy.edit">
                {(canEdit) => (
                    <Table
                        columns={tableColumns}
                        items={dragArray || []}
                        getItemKey={(output) => output.ID}
                        renderRow={(output) => (
                            <>
                                <DragIconTableData output={output}/>
                                <td>
                                    <OutputIsSyncedTitle outputID={output.ID} datalistId="output-unit-suggestions" />
                                </td>
                                <td>
                                    <select data-docid={output.docid} value={output.Unit || ''} onChange={unitHandler}>
                                        <option value="">-- Kies eenheid --</option>
                                        {outputUnits.map(unit => (
                                            <option key={unit} value={unit}>{outputUnitLabels[unit] || unit}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <select data-docid={output.docid} value={output.MeasurementMethod || DEFAULT_MEASUREMENT_METHOD} onChange={methodHandler}>
                                        {methodOptions.map(method => (
                                            <option key={method} value={method}>{measurementMethodLabels[method]}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="common-table__cell--description">
                                    <Tooltip content={output.Description || ''} width='80%' top='-60px'>
                                        <textarea
                                            className="common-table__description-textarea"
                                            data-docid={output.docid}
                                            defaultValue={output.Description || ''}
                                            placeholder="Beschrijving"
                                            onChange={descriptionHandler}
                                        />
                                    </Tooltip>
                                </td>
                                <td>
                                    <OutputGoalCell output={output} onOpen={openGoalModal} />
                                </td>
                                <td>
                                    {canEdit && (
                                        <OutputIsSyncedDelete outputID={output.ID}/>
                                    )}
                                </td>
                            </>
                        )}
                        searchAccessor={(output) => output.Title}
                        searchPlaceholder="Zoeken op output"
                        emptyMessage="Geen outputs gevonden."
                        draggable={true}
                        droppableId="outputs"
                        onDragEnd={handleDragEnd}
                        addRow={canEdit ? {
                            toolTipContent: 'Output toevoegen',
                            onClick: addOutput,
                        } : undefined}
                    />
                )}
            </ReadOnlyGuard>
            <div className="impactstrategy-saved-and-nextstep-container">
                <SavedNotice />
                <div id='next-step-container'>
                    <PreviousStepContainer url={'Activities'} mainItem={'impactstrategy'}/>
                    <NextStepContainer url={'effects'} mainItem={'impactstrategy'}/>
                </div>
            </div>
        </div>
        {goalOutput && (
            <OutputGoalModal
                modalOpen={goalModalOpen}
                setModalOpen={setGoalModalOpen}
                output={goalOutput}
                onSaved={setSaved}
            />
        )}
    </div>
  )
}

export default AddOutputs
